import React from "react";

import { Box, Button, Container, Stack, Typography } from "@mui/material";
import {
  getColorWithMode,
  getBorder,
  getMutedText,
  HEADING_FONT,
} from "../constants/colors";
import { useMediaQuery } from "react-responsive";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { ArrowLeft, Download, Printer } from "lucide-react";
import { exportToCSV } from "../utils/pack";

const HEADER_BUTTON_SX = {
  borderRadius: "9px",
  fontFamily: HEADING_FONT,
  fontWeight: 600,
  textTransform: "none",
  whiteSpace: "nowrap",
};

const createTableStructure = (tableData) => {
  return tableData.reduce((acc, curr, index) => {
    const { title, rows } = curr;
    const sectionRows = Object.values(rows).map(
      ({ id, item, price, weight, unit, quantity, category, isSelected }) => {
        return {
          id,
          item,
          price,
          weight,
          unit,
          quantity,
          hierarchy: [title, id],
          category,
          categoryIndex: index,
          selected: isSelected ? isSelected : false,
        };
      }
    );
    return [...acc, ...sectionRows];
  }, []);
};

const getTreeDataPath = (row) => row.hierarchy;

const CustomGridTreeDataGroupingCell = (props) => {
  const { row, rowNode } = props;
  return (
    <Box
      sx={{
        fontSize: rowNode.isAutoGenerated ? "16px" : "",
        fontFamily: HEADING_FONT,
        fontWeight: rowNode.isAutoGenerated ? 700 : 400,
        letterSpacing: rowNode.isAutoGenerated ? "-0.01em" : "",
      }}
    >
      {rowNode.isAutoGenerated ? rowNode.groupingKey : row.item}
    </Box>
  );
};

const PackChecklistContainer = ({
  packName,
  tableData,
  darkMode,
  handleLeaveChecklist,
  modifyPack,
}) => {
  const [rowSelectionModel, setRowSelectionModel] = React.useState([]);
  const [rows, setRows] = React.useState([]);

  const isMobile = useMediaQuery({ maxWidth: 898 });
  const isDesktopOrLaptop = useMediaQuery({ minWidth: 1380 });
  const apiRef = useGridApiRef();

  React.useEffect(() => {
    const newRows = createTableStructure(tableData);
    const selectedRowInitialState = newRows.reduce((acc, curr) => {
      if (curr.selected) acc.push(curr.id);
      return acc;
    }, []);
    setRows(newRows);
    setRowSelectionModel(selectedRowInitialState);
  }, [tableData]);

  const columns = [
    {
      field: "category",
      headerName: "Item Type",
      width: 120,
      maxWidth: 200,
      flex: 1,
      editable: false,
      headerAlign: "left",
    },
    {
      field: "quantity",
      headerName: "Qty",
      width: 50,
      editable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "weight",
      headerName: "Weight",
      width: 100,
      editable: false,
      headerClassName: "lastcolumnSeparator",
      headerAlign: "center",
      align: "center",
      valueGetter: (params) => {
        return `${params.row.weight || ""}${
          params.row.weight && params.row.unit ? params.row.unit : ""
        }`;
      },
    },
  ];

  const handleSetRowSelectionModel = (newRowSelectionModel) => {
    const selectedRows = rows.filter(({ id }) =>
      newRowSelectionModel.includes(id)
    );
    const rowsToUpdate = selectedRows.map(({ categoryIndex, id }) => ({
      categoryIndex,
      id,
    }));
    const newData = [...tableData];
    const formattedSelection = rowsToUpdate.reduce((acc, curr) => {
      if (acc[curr.categoryIndex]) {
        acc[curr.categoryIndex].push(String(curr.id));
      } else {
        acc[curr.categoryIndex] = [String(curr.id)];
      }
      return acc;
    }, {});
    for (let i = 0; i < newData.length; i++) {
      const keys = Object.keys(newData[i].rows);
      for (let j = 0; j < keys.length; j++) {
        if (newData[i].rows[keys[j]]) {
          if (formattedSelection[i]) {
            newData[i].rows[String(keys[j])].isSelected =
              formattedSelection[i] &&
              formattedSelection[i].includes(String(keys[j]));
          } else {
            newData[i].rows[String(keys[j])].isSelected = false;
          }
        }
      }
    }
    modifyPack(newData);
    setRowSelectionModel(newRowSelectionModel);
  };

  return (
    <Container
      fixed
      style={{
        backgroundColor: getColorWithMode(darkMode, "base"),
        height: !isMobile ? "calc(100vh - 50px)" : "calc(100vh - 260px)",
        width: isDesktopOrLaptop ? "1140px" : isMobile ? "100%" : "880px",
        minHeight: "920px",
        maxHeight: "calc(100vh - 50px)",
        overflowY: "scroll",
        marginLeft: "0px",
        paddingBottom: "20px",
        paddingLeft: "0px",
        margin: "0 auto",
      }}
    >
      <Container sx={{ marginTop: "5px" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          sx={{
            maxWidth: "770px",
            margin: "0 auto",
            paddingTop: "10px",
          }}
        >
          <Button
            startIcon={<ArrowLeft size={18} />}
            onClick={handleLeaveChecklist}
            sx={HEADER_BUTTON_SX}
          >
            Back to Pack
          </Button>
          <Typography
            noWrap
            sx={{
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              fontSize: isMobile ? "24px" : "20px",
              letterSpacing: "-0.02em",
              maxWidth: isMobile ? "100%" : "320px",
            }}
          >
            {packName}
          </Typography>
          <Stack direction="row" gap={1}>
            <Button
              variant="outlined"
              startIcon={<Download size={16} />}
              onClick={() => {
                exportToCSV(packName, tableData);
              }}
              sx={HEADER_BUTTON_SX}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              startIcon={<Printer size={16} />}
              onClick={() => {
                apiRef.current.exportDataAsPrint({
                  hideFooter: true,
                  hideToolbar: true,
                  fileName: packName,
                });
              }}
              sx={HEADER_BUTTON_SX}
            >
              Print
            </Button>
          </Stack>
        </Stack>
        <DataGridPro
          sx={{
            margin: "0 auto",
            width: "770px",
            marginTop: "12px",
            borderRadius: "12px",
            borderColor: getBorder(darkMode),
            "& .MuiDataGrid-columnHeaderTitle": {
              fontFamily: HEADING_FONT,
              fontWeight: 600,
              fontSize: isMobile ? "13px" : "11px",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: getMutedText(darkMode),
            },
            "& .MuiDataGrid-columnHeaders": {
              borderColor: getBorder(darkMode),
            },
            "& .MuiDataGrid-cell": {
              borderColor: getBorder(darkMode),
              fontFamily: HEADING_FONT,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: darkMode
                ? "rgba(144,202,249,0.06)"
                : "rgba(25,118,210,0.04)",
            },
            ".Mui-disabled": {
              color: "transparent !important",
            },
            ".MuiDataGrid-row.shouldStrikeThrough": {
              textDecoration: "line-through",
            },
            "@media print": {
              padding: "5px 5px 5px 5px",
              backgroundColor: "#FFFFFF !important",
              color: "rgba(0, 0, 0, 0.87) !important",
              border: "0px !important",
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "rgba(0, 0, 0, 0.6) !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#FFFFFF !important",
                borderColor: "rgba(0, 0, 0, 0.12) !important",
              },
              "& .MuiDataGrid-cell": {
                color: "rgba(0, 0, 0, 0.87) !important",
                borderColor: "rgba(0, 0, 0, 0.12) !important",
              },
              ".MuiDataGrid-virtualScrollerContent": {
                minHeight: `calc(${rows.length} * 50px) !important`,
                color: "rgba(0, 0, 0, 0.87)",
                backgroundColor: "#FFFFFF !important",
              },
              ".MuiDataGrid-row": {
                minHeight: "40px !important",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "transparent !important",
              },
              ".MuiCheckbox-root": {
                color: "rgba(0, 0, 0, 0.6) !important",
              },
              "& .MuiSvgIcon-root": {
                color: "rgba(0, 0, 0, 0.6) !important",
              },
              ".Mui-disabled": {
                color: "transparent !important",
              },
            },
          }}
          apiRef={apiRef}
          density="compact"
          autoHeight
          disableSelectionOnClick
          rows={rows}
          getRowClassName={(params) =>
            rowSelectionModel.find((id) => id === params.row.id)
              ? "shouldStrikeThrough"
              : ""
          }
          columns={columns}
          getTreeDataPath={getTreeDataPath}
          treeData
          hideFooter
          checkboxSelection
          defaultGroupingExpansionDepth={-1}
          groupingColDef={{
            headerName: "Name",
            hideDescendantCount: true,
            flex: 1,
            valueFormatter: (params) => {
              return params.value;
            },
            valueGetter: (params) => {
              return params.rowNode.isAutoGenerated
                ? params.rowNode.groupingKey
                : params.row.item;
            },
            renderCell: (params) => (
              <CustomGridTreeDataGroupingCell {...params} />
            ),
          }}
          isRowSelectable={(params) => Object.keys(params.row).length}
          onSelectionModelChange={(newRowSelectionModel) => {
            handleSetRowSelectionModel(newRowSelectionModel);
          }}
          selectionModel={rowSelectionModel}
          components={{
            NoRowsOverlay: () => (
              <Box
                sx={{
                  textAlign: "center",
                  verticalAlign: "center",
                  lineHeight: "72px",
                  color: "#999",
                }}
              >
                No Items
              </Box>
            ),
          }}
        />
      </Container>
    </Container>
  );
};

export default PackChecklistContainer;
